import React from "react";
import PropTypes from "prop-types";
import { Utilities } from "@learningblocks/exp-constants";

const { throttle, VIEW_TYPE } = Utilities;

const QUERY_WEB = "only screen and (min-width: 1024px)";
const QUERY_TAB = "only screen and (min-width: 768px) and (max-width: 1023px)";
const QUERY_MOB = "only screen and (min-width: 320px) and (max-width: 767px)";

const MEDIA_WEB = window.matchMedia(QUERY_WEB);
const MEDIA_TAB = window.matchMedia(QUERY_TAB);
const MEIDA_MOB = window.matchMedia(QUERY_MOB);

const EVENT_RESIZE = "resize";
const THROTTLE_TIME = 100;

const MediaType = {};

/** MediaType.Context:
 * - Provides a React.Context for MediaType
 */
MediaType.Context = React.createContext({});

/**
 * MediaType.Provider:
 *  - Provides a React.Context Wrapper
 *    The component listen to MediaQueryUpdates and return VIEW_TYPE [ 'WEB', 'MOB' 'TAB']
 */
MediaType.Provider = (props) => {
  const [type, setType] = React.useState(VIEW_TYPE.WEB);

  const matchWeb = React.useCallback(() => MEDIA_WEB.matches, []);

  const matchTab = React.useCallback(() => MEDIA_TAB.matches, []);

  const matchMob = React.useCallback(() => MEIDA_MOB.matches, []);

  const setNewType = React.useCallback(() => {
    switch (true) {
      case matchMob(): {
        setType(VIEW_TYPE.MOB);
        break;
      }
      case matchTab(): {
        setType(VIEW_TYPE.TAB);
        break;
      }
      case matchWeb():
      default:
        setType(VIEW_TYPE.WEB);
    }
  }, [setType, matchWeb, matchTab, matchMob]);

  const onResize = React.useCallback(() => {
    throttle(setNewType, THROTTLE_TIME);
  }, [setNewType]);

  React.useEffect(() => {
    setNewType();
    window.addEventListener(EVENT_RESIZE, onResize);
    return () => window.removeEventListener(EVENT_RESIZE, onResize);
  }, [onResize, setNewType]);

  return (
    <MediaType.Context.Provider value={type}>
      {props.children}
    </MediaType.Context.Provider>
  );
};

MediaType.Provider.propTypes = {
  /**
   * Root node for all child components that need to use MediaType Context
   */
  children: PropTypes.element,
};

export { MediaType };
